import api, { PresencasResponse } from 'api/Assessor/evento';
import translation from 'locales/translation';

import { ChatIcon, EditIcon, PeopleIcon, PlayIcon } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import AddAttachment from 'components/Modal/AddAttachment';
import { AdicionarConvidado } from 'components/Modal/AdicionarConvidado/AdicionarConvidado';
import CadastroPautaModal from 'components/Modal/CadastroPautaModal/CadastroPautaModal';
import ChangeEventSituation from 'components/Modal/ChangeEventSituation';
import { ConfirmarPresenca } from 'components/Modal/ConfirmarPresenca';
import InfoAssembly from 'components/Modal/InfoAssembly';
import VideoAssembly from 'components/Modal/VideoAssembly';
import { ITopBarMenuItem } from 'components/__SHARED__/TopBar/TopBarMenuItem';
import { ApiErrors } from 'helpers/enums/ApiErrors';
import { AssemblyModalitiesNumber } from 'helpers/enums/Assembleia';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import { TopBarMenuAcessoRapido } from 'helpers/enums/TopBarMenuAcessoRapido';
import AxiosErrorHandler from 'helpers/utils/AxiosErrorHandler';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { getAcessoRapido } from 'helpers/utils/TopBarMenuUtils';
import {
  AssessorRelatoriosRoutePath,
  AssessorRoutePath,
  LiberarPautaEmLoteAssessorRoutePath,
  ReordenarPautasRoutePath,
  VotacaoPresencialRoutePath,
  VotoPresencialPath,
} from 'library/Routes';
import UnauthorizedScreen from 'pages/Errors/UnauthorizedScreen/UnauthorizedScreen';
import {
  RefreshIcon,
  ScreenSize,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  assembleiasAssessorActionsAtualizarPresencas,
  assembleiasAssessorActionsCheckQuorum,
  assembleiasAssessorActionsLoadEventoList,
  assembleiasAssessorActionsRegistraQuorum,
  assembleiasAssessorActionsStartVoting,
  assembleiasAssessorLoadEvento,
} from 'store/modules/AssembleiasAssessorActions';
import { ASSEMBLEIAS_ASSESSOR_UPDATE_VOTATION } from 'store/modules/AssembleiasAssessorActionTypes';
import { assessorActionCarregarPautas } from 'store/modules/assessor/actions';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import {
  setCurrentWSEvent,
  wsAssembleiasActionsClose,
  wsAssembleiasActionsOpen,
} from 'store/modules/WSAssembleiasActions';
import { IReducer } from 'store/Reducer';
import Context from './Context';
import Evento from './Evento';
import ExcluirModal, { ExcluirModalProps } from './ExcluirModal';
import './styles.scss';

interface RouteParams {
  id: string;
}

export default function AssembleiaAssessor() {
  const { id } = useParams<RouteParams>();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { showToast, setPageMenuItems, startLoading, finishLoading } =
    useContext(LayoutContext);
  const { evento, loadingEvento, contasDisponiveis, loadingPautas } =
    useSelector((state: IReducer) => state.assembleiasAssessor);
  const { opened: wsOpened } = useSelector(
    (state: IReducer) => state.wsAssembleias
  );
  const { usuario } = useSelector((state: IReducer) => state.auth);
  const [bloqueando, setBloqueando] = useState<boolean>(false);
  const [atualizandoPresencas, setAtualizarPresencas] =
    useState<boolean>(false);
  const [presencas, setPresencas] = useState<PresencasResponse>({
    contasCapital: 0,
    presentes: 0,
    convidados: 0,
    presentesAptos: 0,
    quorumAtingido: false,
    quantidadePessoasOnline: 0,
  });
  const [registrandoQuorum, setRegistrandoQuorum] = useState<boolean>(false);
  const [startingVoting, setStartingVoting] = useState<boolean>(false);
  const [pagePautas, setPagePautas] = useState<number>(0);
  const [pautas, setPautas] = useState<number>(0);

  const bloquearEvento = useCallback(async () => {
    if (!evento) return;
    setBloqueando(true);
    startLoading('Aguarde, evento sendo bloqueado.');
    const { fail, response } = await api.evento.bloquear(evento.id);
    if (fail) {
      finishLoading();
      setBloqueando(false);
      ErrorHandler(fail);
    }
    if (response && response.status === 200) {
      dispatch(
        assembleiasAssessorLoadEvento(
          evento.id,
          () => setBloqueando(false),
          (error) => {
            setBloqueando(false);
            ErrorHandler(error.response!.data);
          }
        )
      );
    }
  }, [evento, dispatch, startLoading, finishLoading]);

  const alterarSituacao = useCallback(() => {
    if (!evento) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(ChangeEventSituation, {
        modalKey: modalKey,
        event: evento,
        preventMaskExit: true,
        modo: evento.modo,
      })
    );
  }, [dispatch, evento]);

  const atualizarPresencas = useCallback(async () => {
    if (!evento?.id || evento.id !== parseInt(id)) return;
    setAtualizarPresencas(true);
    dispatch(
      assembleiasAssessorActionsAtualizarPresencas(
        evento.id,
        (data) => {
          setPresencas(data);
          setAtualizarPresencas(false);
        },
        (fail) => {
          ErrorHandler(fail);
          setAtualizarPresencas(false);
        }
      )
    );
  }, [evento, dispatch, id]);

  const openEditarTransmissao = useCallback(() => {
    if (!evento) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(InfoAssembly, {
        modalKey: modalKey,
        evento: evento,
        modo: evento.modo,
        preventMaskExit: true,
      })
    );
  }, [dispatch, evento]);

  const openDocumentos = useCallback(() => {
    if (!evento) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(AddAttachment, {
        eventId: Number(evento.id),
        registeting: false,
        modalKey: modalKey,
        preventEscExit: true,
        preventMaskExit: true,
        mobileOn: ScreenSize.xs,
        shouldUseBff: false,
        onClose: () => {
          closeModal(id);
        },
        onConfirm: () =>
          new Promise<void>(() => {
            closeModal(id);
          }),
      })
    );
  }, [dispatch, evento, id]);

  const isButtonCadastroPautaDisabled = useMemo(() => {
    if (!evento) return true;
    if (loadingEvento) return true;
    if (evento.status >= AssembleiaStatus.Encerrado) return true;
    if (
      (usuario?.perfil.id === 'pertencer_consulta' &&
        !usuario?.perfil.assessorRoute.assembleias.cadastroPauta.cadastrar &&
        evento?.tipo !== 'REU') ||
      (usuario?.perfil.id !== 'pertencer_consulta' &&
        !usuario?.perfil.assessorRoute.assembleias.cadastroPauta.cadastrar)
    )
      return true;
    return false;
  }, [evento, usuario, loadingEvento]);

  const openCadastroPautaModal = useCallback(() => {
    if (!evento) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(CadastroPautaModal, {
        modalKey: modalKey,
        idEvento: evento.id,
        status: evento.status,
        cooperativa: evento?.cooperativa,
        currentPage: pagePautas,
        preventMaskExit: true,
        mobileOn: ScreenSize.xs,
        onClose: () => dispatch(closeModal(modalKey)),
      })
    );
  }, [dispatch, evento, pagePautas]);

  const openNewTabVotacao = useCallback(() => {
    const url = VotoPresencialPath.replace(':idAssembleia', id);
    const win = window.open(url, '_blank');
    win!.focus();
  }, [id]);

  const contadorContasDisabled = useMemo((): boolean => {
    if (!evento || loadingEvento) return true;
    if (
      evento.status === AssembleiaStatus.Live &&
      evento.modo === AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA
    ) {
      return false;
    }
    return true;
  }, [evento, loadingEvento]);

  const openConfirmarPresenca = useCallback(() => {
    if (!evento || !usuario) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(ConfirmarPresenca, {
        modalKey: modalKey,
        preventEscExit: true,
        preventMaskExit: true,
        mobileOn: ScreenSize.xs,
        eventId: evento.id,
        eventMode: evento.modo,
        title: translation.ConfirmarPresencaModal.title,
        onClose: () => {
          dispatch(closeModal(modalKey));
        },
        onConfirm: () => {
          return new Promise<void>((resolve: any) => {
            resolve();
            dispatch(closeModal(modalKey));
          });
        },
      })
    );
  }, [dispatch, evento, usuario]);

  const abrirAdicionarConvidado = useCallback(() => {
    if (!evento || !usuario) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(AdicionarConvidado, {
        modalKey: modalKey,
        preventEscExit: true,
        preventMaskExit: true,
        mobileOn: ScreenSize.xs,
        eventId: evento.id,
        title: translation.ConfirmarPresencaModal.title,
        onClose: () => {
          dispatch(closeModal(modalKey));
        },
        onConfirm: () => {
          return new Promise<void>((resolve: any) => {
            resolve();
            dispatch(closeModal(modalKey));
          });
        },
      })
    );
  }, [dispatch, evento, usuario]);

  const openEditarVideosPreAssembleia = useCallback(() => {
    if (!evento) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      openModal(VideoAssembly, {
        eventId: Number(evento.id),
        registering: false,
        modalKey: modalKey,
        preventEscExit: true,
        preventMaskExit: true,
        mobileOn: ScreenSize.xs,
        shouldUseBff: false,
        onClose: () => {
          closeModal(id);
        },
        onConfirm: () =>
          new Promise<void>(() => {
            closeModal(id);
          }),
      })
    );
  }, [dispatch, evento, id]);

  const registraQuorum = useCallback(() => {
    if (!evento) return;
    setRegistrandoQuorum(true);
    dispatch(
      assembleiasAssessorActionsRegistraQuorum(
        evento.id,
        () => setRegistrandoQuorum(false),
        (error) => {
          setRegistrandoQuorum(false);
          ErrorHandler(error);
        }
      )
    );
  }, [evento, dispatch]);

  const startVoting = useCallback(() => {
    if (!evento) return;
    setStartingVoting(true);
    dispatch(
      assembleiasAssessorActionsStartVoting(
        evento.id,
        () => {
          dispatch({
            type: ASSEMBLEIAS_ASSESSOR_UPDATE_VOTATION,
            payload: true,
          });
          setStartingVoting(false);
        },
        (error) => {
          setStartingVoting(false);
          ErrorHandler(error);
        }
      )
    );
  }, [evento, dispatch]);

  const excluirEvento = useCallback(() => {
    if (!evento) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        ExcluirModal,
        {
          genericProps: {
            key: modalKey,
            idEvento: evento.id,
          },
        } as ExcluirModalProps,
        modalKey
      )
    );
  }, [evento, dispatch]);

  const [liberarEmLote, setLiberarEmLote] = useState<boolean>(false);

  const subMenu = useMemo<ITopBarMenuItem[]>(() => {
    if (!evento || !usuario) return [];

    const menus: ITopBarMenuItem[] = getAcessoRapido(
      translation,
      usuario.perfil,
      TopBarMenuAcessoRapido.Assembleias,
      id,
      evento.tipo
    );

    if (
      (usuario?.perfil.id === 'pertencer_consulta' &&
        !usuario.perfil.assessorRoute.assembleias.videos.editar &&
        evento?.tipo === 'REU') ||
      usuario.perfil.assessorRoute.assembleias.videos.editar
    ) {
      menus.push({
        label: translation.SubMenus.evento,
        dataTestId: 'submenu-event',
        subItems: [
          {
            label: translation.SubMenus.dadosInformativos,
            disabled: loadingEvento,
            onClick: openEditarTransmissao,
            icon: <EditIcon />,
          },
          {
            label: translation.SubMenus.videos,
            disabled: loadingEvento,
            onClick: openEditarVideosPreAssembleia,
            icon: <PlayIcon />,
          },
        ],
      });
    }

    if (
      (usuario?.perfil.id === 'pertencer_consulta' &&
        !usuario.perfil.assessorRoute.assembleias.documentos.editar &&
        evento?.tipo === 'REU') ||
      usuario.perfil.assessorRoute.assembleias.documentos.editar
    ) {
      menus.push({
        label: translation.SubMenus.documentos,
        onClick: openDocumentos,
        disabled: loadingEvento,
        dataTestId: 'submenu-docs',
      });
    }

    menus.push({
      label: translation.SubMenus.pautas.title,
      disabled: isButtonCadastroPautaDisabled,
      dataTestId: 'submenu-guidelines',
      subItems: [
        {
          label: translation.SubMenus.pautas.cadastrarPauta,
          disabled:
            evento.modo ===
              AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL ||
            loadingEvento,
          onClick: openCadastroPautaModal,
          icon: <ChatIcon />,
        },
        {
          label: translation.SubMenus.pautas.liberarEmLote,
          disabled:
            loadingEvento ||
            evento!.status !== AssembleiaStatus.Live ||
            !evento!.flagIniciarVotacaoDigital ||
            ((evento!.tipo === 'AGO' ||
              evento!.tipo === 'AGE' ||
              evento!.tipo === 'AOE') &&
              !evento!.existeQuorum),
          onClick: () => {
            history.push(
              `${LiberarPautaEmLoteAssessorRoutePath.replace(
                ':id',
                String(evento?.id)
              )}`
            );
          },
          icon: <PeopleIcon />,
        },
        {
          label: translation.SubMenus.pautas.reordenarPautas,
          disabled:
            pautas === 0 ||
            pautas === 1 ||
            (evento?.flagIniciarVotacaoDigital &&
              (evento?.status === AssembleiaStatus.NaoDisponivel ||
                evento?.status === AssembleiaStatus.Pre ||
                evento?.status === AssembleiaStatus.Live)),
          onClick: () => {
            history.push(
              `${ReordenarPautasRoutePath.replace(':id', String(evento?.id))}`
            );
          },
          icon: (
            <div className={'reorder'}>
              <RefreshIcon animate={false} />
            </div>
          ),
        },
      ],
    });

    if (evento?.id === parseInt(id)) {
      menus.push({
        label: translation.SubMenus.relatorio,
        onClick: () =>
          window.open(
            AssessorRelatoriosRoutePath.replace(':id', id.toString()),
            '_blank'
          ),
        disabled: false,
      });
    }

    if (!contadorContasDisabled) {
      menus.push({
        label: translation.SubMenus.confirmarPresenca,
        onClick: openConfirmarPresenca,
        disabled: loadingEvento,
        dataTestId: 'submenu-confirm-presence',
      });
      menus.push({
        label: translation.SubMenus.votacaoPresencial,
        onClick: openNewTabVotacao,
        disabled: loadingEvento,
        dataTestId: 'submenu-in-person-voting',
      });
    }

    if (
      evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO &&
      usuario.perfil.assessorRoute.assembleias.adicionarConvidado &&
      ((usuario.perfil.id === 'pertencer_consulta' &&
        !usuario.perfil.associadoRoute.assembleias.live.ver) ||
        (usuario.perfil.id !== 'pertencer_consulta' &&
          usuario.perfil.associadoRoute.assembleias.live)) &&
      evento.status === AssembleiaStatus.Live
    ) {
      menus.push({
        label: translation.SubMenus.adicionarConvidado,
        onClick: abrirAdicionarConvidado,
        disabled: loadingEvento,
        dataTestId: 'submenu-adicionar-convidado',
      });
    }

    if (
      evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO &&
      usuario.perfil.associadoRoute.assembleias.live &&
      evento.status === AssembleiaStatus.Live
    ) {
      menus.push({
        label: translation.SubMenus.confirmarPresenca,
        onClick: openConfirmarPresenca,
        disabled: loadingEvento,
        dataTestId: 'submenu-confirm-presence',
      });
    }

    if (
      evento.modo === AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO &&
      evento.status === AssembleiaStatus.Live
    ) {
      menus.push({
        label: translation.SubMenus.votacaoPresencial,
        onClick: () => {
          history.push(
            `${VotacaoPresencialRoutePath.replace(':id', String(evento?.id))}`
          );
        },
        disabled: loadingEvento,
        dataTestId: 'submenu-votacao-presencial',
      });
    }

    if (usuario.perfil.assessorRoute.assembleias.excluir) {
      menus.push({
        label: translation.SubMenus.excluirEvento.botao,
        onClick: excluirEvento,
        disabled: loadingEvento,
        dataTestId: 'submenu-remove-event-2',
      });
    }

    return menus;
  }, [
    evento,
    usuario,
    id,
    isButtonCadastroPautaDisabled,
    loadingEvento,
    openCadastroPautaModal,
    pautas,
    contadorContasDisabled,
    openEditarTransmissao,
    abrirAdicionarConvidado,
    openEditarVideosPreAssembleia,
    openDocumentos,
    history,
    openConfirmarPresenca,
    openNewTabVotacao,
    excluirEvento,
  ]);

  const loadPautas = useCallback(
    (idEvento: number) => {
      dispatch(
        assessorActionCarregarPautas(idEvento, pagePautas, ErrorHandler)
      );
    },
    [dispatch, pagePautas]
  );

  useEffect(() => {
    if ((!evento || evento.id !== parseInt(id)) && !loadingEvento) {
      dispatch(
        assembleiasAssessorLoadEvento(
          parseInt(id),
          (newEvent) => {
            loadPautas(newEvent.id);
          },
          (error) => {
            dispatch(
              assembleiasAssessorActionsLoadEventoList(
                undefined,
                AxiosErrorHandler
              )
            );
            history.replace(AssessorRoutePath);
            finishLoading();

            if (
              error.response?.data.code === ApiErrors.EventoNaoEncontrado ||
              error.response?.data.message === 'evento.notFound'
            ) {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label: 'Evento não encontrado!',
              });
            } else {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label: 'Houve um erro ao carregar o evento!',
              });
            }
          }
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento, loadingEvento, id, dispatch, finishLoading, history]);

  useEffect(() => {
    evento?.id && loadPautas(evento.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePautas]);

  useEffect(() => {
    atualizarPresencas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento?.id]);

  useEffect(() => {
    if (evento?.needLoadQuorum) {
      dispatch(
        assembleiasAssessorActionsCheckQuorum(
          evento.id,
          undefined,
          ErrorHandler
        )
      );
    }
  }, [dispatch, evento]);

  useEffect(() => {
    if (!evento?.id || !usuario) return;
    if (!wsOpened && evento.id === parseInt(id)) {
      dispatch(
        wsAssembleiasActionsOpen(
          usuario.tokenWs!,
          usuario.cpfCnpj!,
          evento.id,
          false,
          evento.status,
          () => setCurrentWSEvent(evento.id)
        )
      );
    }
    return () => {
      if (window.location.pathname !== location.pathname) {
        dispatch(wsAssembleiasActionsClose());
      }
    };
  }, [evento, usuario, wsOpened, id, dispatch, location]);

  useEffect(() => {
    setPageMenuItems(subMenu);
    return () => setPageMenuItems();
  }, [subMenu, setPageMenuItems]);

  useEffect(() => {
    if (bloqueando) return;
    if ((!evento && loadingEvento) || loadingEvento) {
      startLoading('');
      // startLoading('Aguarde, o evento está sendo carregado.');
    } else {
      finishLoading();
    }
  }, [evento, loadingEvento, startLoading, finishLoading, bloqueando]);

  if (!usuario?.perfil.assessorRoute.assembleias.ver) {
    return <UnauthorizedScreen />;
  }

  return (
    <Context.Provider
      value={{
        evento,
        contasDisponiveis,
        loadingEvento,
        loadingPautas,
        liberarEmLote,
        setLiberarEmLote,
        bloquearEvento,
        bloqueando,
        alterarSituacao,
        usuario,
        atualizandoPresencas,
        atualizarPresencas,
        presencas,
        editarTransmissao: openEditarTransmissao,
        registraQuorum,
        startVoting,
        startingVoting,
        registrandoQuorum,
        pagePautas,
        setPagePautas,
        setPautas,
        loadPautas,
      }}
    >
      <Evento />
    </Context.Provider>
  );
}
